import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Stack,
  Link,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Avatar,
  IconButton,
  Badge,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Roofing as RoofingIcon,
  Home as HomeIcon,
  Straighten as StraightenIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Input as InputIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  TextFields as TextFieldsIcon
} from '@mui/icons-material';
import { responseService } from '../../services/response.service';
import { demandeDetailsService } from '../../services/demande-details.service';
import { httpService } from '../../services/http.service';
import { API_ENDPOINTS } from '../../config/api.config';

// Couleurs des statuts
const getStatusColor = (status) => {
  switch (status) {
    case 'En cours':
      return 'info';
    case 'Etudié':
      return 'success';
    case 'Rejeté':
      return 'error';
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'pending':
      return 'default';
    default:
      return 'default';
  }
};

const getResponseTypeIcon = (contentTypeName) => {
  switch (contentTypeName) {
    case 'PRODUCTS':
      return <HomeIcon />;
    case 'INPUT':
      return <InputIcon />;
    case 'OTHER_CHOICE':
      return <RadioButtonCheckedIcon />;
    case 'TEXT':
      return <TextFieldsIcon />;
    default:
      return <AssignmentIcon />;
  }
};

const getResponseTypeColor = (contentTypeName) => {
  switch (contentTypeName) {
    case 'PRODUCTS':
      return 'success';
    case 'INPUT':
      return 'info';
    case 'OTHER_CHOICE':
      return 'warning';
    case 'TEXT':
      return 'primary';
    default:
      return 'default';
  }
};

const getResponseTypeLabel = (contentTypeName) => {
  switch (contentTypeName) {
    case 'PRODUCTS':
      return 'Produit';
    case 'INPUT':
      return 'Saisie';
    case 'OTHER_CHOICE':
      return 'Choix';
    case 'TEXT':
      return 'Texte';
    default:
      return 'Autre';
  }
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`demande-tabpanel-${index}`}
      aria-labelledby={`demande-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const DemandeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeResponseTab, setActiveResponseTab] = useState(0);
  const [demande, setDemande] = useState(null);
  const [responses, setResponses] = useState([]);
  const [project, setProject] = useState(null);
  const [steps, setSteps] = useState([]);
  const [sections, setSections] = useState([]);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer les données de la demande
  useEffect(() => {
    const fetchDemande = async () => {
      try {
        setLoading(true);
        
        // Récupérer les détails de la demande
        const demandeResult = await demandeDetailsService.getDemandeDetails(parseInt(id));
        
        if (demandeResult.success) {
          setDemande(demandeResult.data);
          
          // Récupérer les données du projet si disponible
          if (demandeResult.data.projet) {
            try {
              const projectData = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(demandeResult.data.projet));
              console.log('Données du projet récupérées:', projectData);
              
              // S'assurer que le projet a un nom
              if (projectData && !projectData.name) {
                projectData.name = projectData.title || projectData.nom || projectData.project_name || `Projet #${demandeResult.data.projet}`;
                console.log('Nom du projet déterminé:', projectData.name);
              }
              
              setProject(projectData);
              
              // Récupérer les étapes du projet
              const stepsData = await httpService.get(API_ENDPOINTS.STEPS.LIST_BY_PROJECT(demandeResult.data.projet));
              console.log('Étapes reçues:', stepsData);
              let stepsArray = Array.isArray(stepsData.results) ? stepsData.results : Array.isArray(stepsData) ? stepsData : [];
              
              // Filtrage côté client pour s'assurer que seules les étapes du projet sont incluses
              const projectId = demandeResult.data.projet;
              stepsArray = stepsArray.filter(step => {
                const stepProjectId = step.projet || step.project_id;
                const isCorrectProject = stepProjectId == projectId || stepProjectId === parseInt(projectId);
                console.log(`Filtrage étape ${step.id}: projet=${stepProjectId}, demandé=${projectId}, correspond=${isCorrectProject}`);
                return isCorrectProject;
              });
              
              console.log(`Étapes après filtrage côté client: ${stepsArray.length}/${Array.isArray(stepsData.results) ? stepsData.results.length : Array.isArray(stepsData) ? stepsData.length : 0}`);
              console.log('Étapes filtrées pour le projet:', stepsArray);
              setSteps(stepsArray);

              // Récupérer les sections et contenus pour chaque étape
              if (stepsArray.length > 0) {
                try {
                  const allSections = [];
                  const allContents = [];
                  
                  for (const step of stepsArray) {
                    // Récupérer les sections de cette étape
                    const sectionsData = await httpService.get(API_ENDPOINTS.SECTIONS.LIST_BY_STEP(step.id));
                    const stepSections = Array.isArray(sectionsData.results) ? sectionsData.results : Array.isArray(sectionsData) ? sectionsData : [];
                    
                    // Récupérer les contenus de chaque section
                    for (const section of stepSections) {
                      const contentsData = await httpService.get(API_ENDPOINTS.CONTENTS.LIST_BY_SECTION(section.id));
                      const sectionContents = Array.isArray(contentsData.results) ? contentsData.results : Array.isArray(contentsData) ? contentsData : [];
                      
                      // Ajouter l'ID de l'étape et de la section aux contenus
                      sectionContents.forEach(content => {
                        content.step_id = step.id;
                        content.section_id = section.id;
                      });
                      
                      allSections.push(...stepSections);
                      allContents.push(...sectionContents);
                    }
                  }
                  
                  setSections(allSections);
                  setContents(allContents);
                  console.log('Sections récupérées:', allSections);
                  console.log('Contenus récupérés:', allContents);
                } catch (sectionsError) {
                  console.warn('Erreur lors de la récupération des sections/contenus:', sectionsError);
                }
              }
            } catch (projectError) {
              console.warn('Erreur lors de la récupération du projet:', projectError);
            }
          }
        } else {
          setError('Erreur lors du chargement de la demande: ' + demandeResult.error);
          return;
        }
        
        // Récupérer les réponses
        try {
          const responsesData = await responseService.getResponses(parseInt(id));
          console.log('Réponses reçues:', responsesData);
          setResponses(Array.isArray(responsesData) ? responsesData : []);
          setActiveResponseTab(0); // Réinitialiser l'onglet des réponses
        } catch (responseError) {
          console.warn('Erreur lors de la récupération des réponses:', responseError);
          setResponses([]);
          setActiveResponseTab(0);
        }
        
      } catch (err) {
        setError('Erreur lors du chargement de la demande');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDemande();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleResponseTabChange = (event, newValue) => {
    setActiveResponseTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Organiser les réponses par étapes et contenus selon le modèle BeResponse
  const organizeResponsesBySteps = () => {
    if (!steps.length) return {};

    const organizedResponses = {};
    
    // Initialiser toutes les étapes du projet (pas seulement celles avec des réponses)
    steps.forEach(step => {
      organizedResponses[step.id] = {
        step: step,
        responses: [],
        sections: []
      };
    });

    // Récupérer les sections pour chaque étape
    steps.forEach(step => {
      const stepSections = sections.filter(section => section.step_id === step.id);
      if (organizedResponses[step.id]) {
        organizedResponses[step.id].sections = stepSections;
      }
    });

    // Distribuer les réponses aux étapes appropriées selon le modèle BeResponse
    if (responses.length > 0) {
      responses.forEach((response, index) => {
        // Vérifier que la réponse est un objet valide selon BeResponse
        if (!response || typeof response !== 'object') {
          console.warn(`Réponse invalide à l'index ${index}:`, response);
          return;
        }

        // Vérifier que la réponse a les champs requis du modèle BeResponse
        if (!response.input_field_name || !response.content_type_name || !response.value) {
          console.warn(`Réponse incomplète à l'index ${index}:`, response);
          return;
        }

        // Extraire l'ID du contenu depuis input_field_name selon le format BeResponse
        // Format: "product_123_0", "input_456_1", "other_choice_789_0", "text_101_0"
        const match = response.input_field_name.match(/(product|input|other_choice|text)_(\d+)_/);
        if (match) {
          const contentId = parseInt(match[2]);
          
          // Trouver l'étape appropriée via le contenu
          const content = contents.find(c => c.id === contentId);
          if (content && content.step_id && organizedResponses[content.step_id]) {
            organizedResponses[content.step_id].responses.push(response);
            console.log(`Réponse ${response.id} assignée à l'étape ${content.step_id} via le contenu ${contentId}`);
          } else {
            console.warn(`Contenu ${contentId} non trouvé ou étape ${content?.step_id} non trouvée pour la réponse ${response.id}`);
            // Si on ne trouve pas le contenu, essayer de distribuer selon l'ordre des étapes
            const stepIndex = contentId % steps.length;
            const step = steps[stepIndex];
            if (step && organizedResponses[step.id]) {
              organizedResponses[step.id].responses.push(response);
              console.log(`Réponse ${response.id} assignée à l'étape ${step.id} par fallback`);
            }
          }
        } else {
          console.warn(`Format input_field_name non reconnu pour la réponse ${response.id}: ${response.input_field_name}`);
          // Si le pattern ne correspond pas, distribuer selon l'ordre des étapes
          const stepIndex = index % steps.length;
          const step = steps[stepIndex];
          if (step && organizedResponses[step.id]) {
            organizedResponses[step.id].responses.push(response);
            console.log(`Réponse ${response.id} assignée à l'étape ${step.id} par distribution`);
          }
        }
      });
    }

    console.log('Réponses organisées par étapes selon BeResponse:', organizedResponses);
    return organizedResponses;
  };

  const renderInformationsGenerales = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              <InfoIcon sx={{ mr: 1 }} />
              Informations du demandeur
            </Typography>
            <Stack spacing={2}>
              <Typography><strong>Nom :</strong> {demande.name}</Typography>
              <Typography><strong>Email :</strong> {demande.email}</Typography>
              <Typography><strong>Téléphone :</strong> {demande.numero_tel}</Typography>
              <Typography><strong>Adresse :</strong> {demande.adresse}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              <AssignmentIcon sx={{ mr: 1 }} />
              Informations du projet
            </Typography>
            <Stack spacing={2}>
              <Typography><strong>Étape :</strong> {demande.project_step}</Typography>
              <Typography><strong>Date de début :</strong> {formatDate(demande.project_start_date)}</Typography>
              <Typography><strong>Référence chantier :</strong> {demande.construction_site_ref}</Typography>
              <Typography><strong>Technicien assigné :</strong> {demande.has_technician ? 'Oui' : 'Non'}</Typography>
              <Typography><strong>Besoin technicien :</strong> {demande.need_technician ? 'Oui' : 'Non'}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Informations complémentaires
            </Typography>
            <Typography variant="body1">
              {demande.complementary_info || 'Aucune information complémentaire'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderReponses = () => {
    const organizedResponses = organizeResponsesBySteps();
    const stepIds = Object.keys(organizedResponses);

    if (stepIds.length === 0) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              Aucune étape trouvée pour ce projet.
            </Alert>
          </Grid>
        </Grid>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
          <CheckCircleIcon sx={{ mr: 1 }} />
          Étapes du projet et éléments soumis
        </Typography>
        
        <Paper elevation={2}>
          <Tabs 
            value={activeResponseTab} 
            onChange={handleResponseTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontWeight: 'bold'
              }
            }}
          >
            {stepIds.map((stepId, index) => {
              const stepData = organizedResponses[stepId];
              const step = stepData.step;
              const stepResponses = stepData.responses;
              const stepSections = stepData.sections;
              
              return (
                <Tab
                  key={stepId}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Badge
                        badgeContent={stepResponses.length}
                        color={stepResponses.length > 0 ? "primary" : "default"}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            height: 20,
                            minWidth: 20,
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <Box width={8} height={8} />
                      </Badge>
                      {step.title}
                    </Box>
                  }
                  icon={<AssignmentIcon />}
                  iconPosition="start"
                />
              );
            })}
          </Tabs>

          {stepIds.map((stepId, index) => {
            const stepData = organizedResponses[stepId];
            const step = stepData.step;
            const stepResponses = stepData.responses;
            const stepSections = stepData.sections;

            return (
              <TabPanel key={stepId} value={activeResponseTab} index={index}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {step.title}
                  </Typography>
                  {step.subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {step.subtitle}
                    </Typography>
                  )}

                  {/* Afficher les sections de cette étape */}
                  {stepSections.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                        Sections de cette étape
                      </Typography>
                      <Grid container spacing={2}>
                        {stepSections.map((section, sectionIndex) => (
                          <Grid item xs={12} md={6} key={section.id}>
                            <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                              <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {section.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Ordre: {section.order}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  
                  {/* Afficher les réponses pour cette étape */}
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                      Éléments soumis dans cette étape
                    </Typography>
                    
                    {stepResponses.length > 0 ? (
                      <Stack spacing={2}>
                        {stepResponses.map((response, responseIndex) => (
                          <Card key={responseIndex} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    icon={getResponseTypeIcon(response.content_type_name)}
                                    label={getResponseTypeLabel(response.content_type_name)}
                                    color={getResponseTypeColor(response.content_type_name)}
                                    size="small"
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    ID: {response.id}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(response.submitted_at)}
                                </Typography>
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary" mb={1}>
                                <strong>Champ d'entrée:</strong> {response.input_field_name}
                              </Typography>
                              
                              <Typography variant="body1" fontWeight="bold" mb={1}>
                                Valeur de la réponse :
                              </Typography>
                              <Box 
                                sx={{ 
                                  p: 2, 
                                  bgcolor: 'white', 
                                  borderRadius: 1, 
                                  border: '1px solid',
                                  borderColor: 'grey.300'
                                }}
                              >
                                {response.value}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        Aucun élément soumis pour cette étape.
                      </Alert>
                    )}
                  </Box>
                </Box>
              </TabPanel>
            );
          })}
        </Paper>
      </Box>
    );
  };

  const renderDocuments = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              <AttachFileIcon sx={{ mr: 1 }} />
              Documents associés
            </Typography>
            
            {demande.documents && demande.documents.length > 0 ? (
              <Grid container spacing={2}>
                {demande.documents.map((docId, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachFileIcon color="primary" />
                            <Typography variant="body2" fontWeight="bold">
                              Document #{docId}
                            </Typography>
                          </Box>
                          <IconButton size="small" color="primary">
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                Aucun document associé à cette demande.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !demande) {
    return (
      <Box p={4}>
        <Typography variant="h5" color="error">
          {error || 'Demande non trouvée'}
        </Typography>
        <Button onClick={() => navigate('/demandes')} sx={{ mt: 2 }} variant="outlined">
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <div className="content-body">
      <div className="container-fluid">
        {/* Header de la demande */}
        <Box sx={{ bgcolor: 'background.paper', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
                  <AssignmentIcon />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold">
                  Demande #{demande.id}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {demande.name} • {demande.project_step}
                  {project && ` • ${project.name}`}
                </Typography>
              </Grid>
              <Grid item>
                <Chip 
                  label={demande.status} 
                  color={getStatusColor(demande.status)}
                  sx={{ color: 'white', fontWeight: 'bold' }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Informations rapides */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Demandeur</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {demande.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Étape</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {demande.project_step}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Date soumission</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatDate(demande.submitted_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Projet</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {project ? project.name : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Onglets */}
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontWeight: 'bold'
                }
              }}
            >
              <Tab
                label="Informations générales"
                icon={<InfoIcon />}
                iconPosition="start"
              />
              <Tab
                label="Réponses et sélections"
                icon={<CheckCircleIcon />}
                iconPosition="start"
              />
              <Tab
                label="Documents"
                icon={<AttachFileIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Contenu des onglets */}
          <TabPanel value={activeTab} index={0}>
            {renderInformationsGenerales()}
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            {renderReponses()}
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            {renderDocuments()}
          </TabPanel>
        </Paper>

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate('/demandes')}
          >
            Voir toutes les demandes
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/demande/${demande.id}/edit`)}
          >
            Modifier la demande
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default DemandeDetailsPage;